import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowUpRight, Check, ChevronRight } from "lucide-react";
import { INDUSTRIES, CASE_STUDY_DISCLAIMER } from "../lib/industriesData";
import { SERVICES_DATA } from "../lib/servicesData";
import { useSEO } from "../lib/useSEO";
import { organisationSchema, SITE_URL } from "../lib/seo";

export default function IndustryDetailPage() {
    const { slug } = useParams();
    const ind = INDUSTRIES[slug];

    useSEO({
        title: ind?.metaTitle,
        description: ind?.metaDescription,
        path: `/industries/${slug}`,
    });

    if (!ind) return <Navigate to="/industries" replace />;

    const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
            { "@type": "ListItem", position: 2, name: "Industries", item: `${SITE_URL}/industries` },
            { "@type": "ListItem", position: 3, name: ind.title, item: `${SITE_URL}/industries/${slug}` },
        ],
    };

    const faqSchema = (ind.faqs && ind.faqs.length > 0) ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: ind.faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
    } : null;

    return (
        <div data-testid={`industry-page-${slug}`}>
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
            {faqSchema && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />}
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(organisationSchema) }} />

            <nav data-testid={`industry-breadcrumb-${slug}`} className="border-b border-brand-border bg-brand-stone/60">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-4 flex items-center gap-2 text-xs text-brand-ink/60">
                    <Link to="/" className="hover:text-brand-navy">Home</Link>
                    <ChevronRight size={12} className="text-brand-ink/40" />
                    <Link to="/industries" className="hover:text-brand-navy">Industries</Link>
                    <ChevronRight size={12} className="text-brand-ink/40" />
                    <span className="text-brand-navy font-medium truncate">{ind.title}</span>
                </div>
            </nav>

            <section className="border-b border-brand-border">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20 lg:py-28">
                    <span className="text-xs uppercase tracking-[0.25em] text-brand-champagne font-medium">{ind.overline}</span>
                    <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl mt-6 leading-[1.02] tracking-tight text-brand-navy">{ind.h1}</h1>
                    <p className="mt-7 text-lg leading-relaxed text-brand-ink/75 max-w-3xl">{ind.intro}</p>
                    <div className="mt-10">
                        <Link to="/booking" data-testid={`industry-cta-${slug}`} className="inline-flex items-center gap-2 px-7 py-3.5 bg-brand-navy text-brand-stone text-sm tracking-wide hover:bg-brand-hover transition-colors">
                            Book a Consultation <ArrowUpRight size={16} />
                        </Link>
                    </div>
                </div>
            </section>

            <section className="border-b border-brand-border bg-white">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <span className="text-xs uppercase tracking-[0.25em] text-brand-champagne">Operating shape</span>
                        <h2 className="font-display text-3xl mt-5 text-brand-navy">What this sector typically looks like.</h2>
                    </div>
                    <ul className="lg:col-span-7 space-y-4">
                        {ind.characteristics.map((c, i) => (
                            <li key={c} className="flex gap-4 pb-4 border-b border-brand-border last:border-0 text-brand-ink/80 leading-relaxed">
                                <span className="text-brand-champagne shrink-0">{String(i + 1).padStart(2, "0")}</span>
                                <span>{c}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            <section className="border-b border-brand-border">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <span className="text-xs uppercase tracking-[0.25em] text-brand-champagne">Where we get called in</span>
                        <h2 className="font-display text-3xl mt-5 text-brand-navy">Challenges we are typically asked to look at.</h2>
                    </div>
                    <ul className="lg:col-span-7 space-y-4">
                        {ind.commonChallenges.map((c) => (
                            <li key={c} className="flex gap-4 text-brand-ink/80 leading-relaxed">
                                <Check className="text-brand-champagne shrink-0 mt-1" size={18} strokeWidth={1.5} />
                                <span>{c}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            <section className="border-b border-brand-border bg-white">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20">
                    <span className="text-xs uppercase tracking-[0.25em] text-brand-champagne">Services most relevant here</span>
                    <h2 className="font-display text-3xl sm:text-4xl mt-5 text-brand-navy">Services for {ind.title.toLowerCase()}.</h2>
                    <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-brand-border">
                        {ind.services.map((s) => {
                            const svc = SERVICES_DATA[s];
                            if (!svc) return null;
                            return (
                                <Link key={s} to={`/services/${s}`} data-testid={`industry-service-${s}`} className="bg-brand-stone p-8 hover:bg-white transition-colors">
                                    <div className="text-xs uppercase tracking-[0.2em] text-brand-champagne">Service</div>
                                    <h3 className="font-display text-lg mt-3 text-brand-navy leading-snug">{svc.title}</h3>
                                    <span className="mt-4 inline-flex items-center gap-1 text-sm text-brand-navy link-underline">Read more <ArrowUpRight size={14} /></span>
                                </Link>
                            );
                        })}
                    </div>
                </div>
            </section>

            {ind.featuredCaseStudy && (
                <section className="border-b border-brand-border" data-testid={`industry-case-study-${slug}`}>
                    <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20">
                        <span className="text-xs uppercase tracking-[0.25em] text-brand-champagne">Featured Case Study</span>
                        <h2 className="font-display text-3xl sm:text-4xl mt-5 text-brand-navy">{ind.featuredCaseStudy.title}</h2>
                        <div className="mt-4 text-xs uppercase tracking-[0.2em] text-brand-ink/55">
                            {ind.featuredCaseStudy.client}
                        </div>

                        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8 max-w-6xl">
                            <div className="lg:col-span-4">
                                <div className="text-[10px] uppercase tracking-[0.25em] text-brand-champagne font-medium">Business Type</div>
                                <p className="mt-2 text-sm text-brand-ink/80 leading-relaxed">{ind.featuredCaseStudy.businessType}</p>
                            </div>
                            <div className="lg:col-span-8 space-y-6">
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.25em] text-brand-champagne font-medium">Challenge</div>
                                    <p className="mt-2 text-sm text-brand-ink/80 leading-relaxed">{ind.featuredCaseStudy.challenge}</p>
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.25em] text-brand-champagne font-medium">Actions Taken</div>
                                    <p className="mt-2 text-sm text-brand-ink/80 leading-relaxed">{ind.featuredCaseStudy.actions}</p>
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.25em] text-brand-champagne font-medium">Outcome</div>
                                    <p className="mt-2 text-sm text-brand-ink/80 leading-relaxed font-medium">{ind.featuredCaseStudy.outcome}</p>
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.25em] text-brand-champagne font-medium">Key Lessons</div>
                                    <p className="mt-2 text-sm text-brand-ink/80 leading-relaxed italic">{ind.featuredCaseStudy.keyLessons}</p>
                                </div>
                            </div>
                        </div>

                        <p className="mt-10 text-xs text-brand-ink/55 italic max-w-3xl">{CASE_STUDY_DISCLAIMER}</p>
                    </div>
                </section>
            )}

            {ind.faqs && ind.faqs.length > 0 && (
                <section className="border-b border-brand-border" data-testid={`industry-faqs-${slug}`}>
                    <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20">
                        <span className="text-xs uppercase tracking-[0.25em] text-brand-champagne">FAQ</span>
                        <h2 className="font-display text-3xl sm:text-4xl mt-5 text-brand-navy">Frequently asked questions.</h2>
                        <div className="mt-12 space-y-6 max-w-4xl">
                            {ind.faqs.map((f) => (
                                <div key={f.q} className="border-b border-brand-border pb-6">
                                    <h3 className="font-display text-lg text-brand-navy">{f.q}</h3>
                                    <p className="mt-3 text-brand-ink/75 leading-relaxed">{f.a}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            <section className="bg-brand-navy">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-8">
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-tight text-brand-stone">
                            Ready to improve operational performance, profitability and guest experience?
                        </h2>
                        <p className="mt-5 text-brand-stone/75 max-w-2xl text-lg">
                            Schedule a confidential consultation with Optimum Hospitality Solutions.
                        </p>
                    </div>
                    <div className="lg:col-span-4 lg:text-right">
                        <Link to="/booking" className="inline-flex items-center gap-2 px-7 py-3.5 bg-brand-champagne text-brand-navy text-sm tracking-wide hover:bg-white">
                            Book a Consultation <ArrowUpRight size={16} />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
